<template>
  <div class="relative agent-connection-mode-selector-dropdown-container" :dir="isRtl === true ? 'rtl' : 'ltr'" :style="selectorStyle">
    <button
      type="button"
      class="btn w-full justify-start font-normal h-auto py-2 px-3 min-h-0 agent-connection-mode-trigger"
      :class="isRtl === true ? 'agent-connection-mode-trigger--rtl' : 'agent-connection-mode-trigger--ltr'"
      @click="toggleDropdown"
      aria-haspopup="listbox"
      :aria-expanded="isOpen !== false"
    >
      <div v-if="selectedModeObject !== null" class="agent-connection-mode-trigger__content">
        <span class="agent-connection-mode-trigger__icon">
          <Icon :icon="selectedModeObject.icon" class="w-4 h-4 text-base-content" />
        </span>
        <div class="agent-connection-mode-trigger__text">
          <span class="agent-connection-mode-trigger__title">{{ selectedModeObject.displayName }}</span>
          <span class="agent-connection-mode-trigger__description">{{ selectedModeObject.description }}</span>
        </div>
        <Icon icon="heroicons:chevron-down-20-solid" class="agent-connection-mode-trigger__chevron w-5 h-5 text-base-content opacity-60" />
      </div>
    </button>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isOpen === true"
        class="absolute z-10 mt-1 w-full bg-base-100 dark:bg-gray-800 shadow-lg rounded-md border border-base-300 dark:border-gray-700 agent-connection-mode-options-list"
        ref="dropdownListRef"
      >
        <div class="py-1 space-y-1">
          <div
            v-for="modeOption in availableModes"
            :key="modeOption.id"
            class="card-as-row mx-1 rounded-md cursor-pointer transition-all duration-150 ease-in-out"
            :class="{
              'bg-primary text-primary-content': modeOption.id === modelValue,
              'hover:bg-base-200 dark:hover:bg-gray-700': modeOption.id !== modelValue
            }"
            @click="selectModeAndClose(modeOption.id)"
          >
            <div class="p-3">
              <div class="agent-connection-mode-option" :class="isRtl === true ? 'agent-connection-mode-option--rtl' : 'agent-connection-mode-option--ltr'">
                <div class="agent-connection-mode-option__icon">
                  <span class="inline-flex justify-center items-center w-8 h-8 rounded-lg bg-base-300 dark:bg-gray-600">
                    <Icon :icon="modeOption.icon" class="w-5 h-5 text-base-content" />
                  </span>
                </div>
                <div class="agent-connection-mode-option__text">
                  <h3 class="agent-connection-mode-option__title" :class="{ 'text-primary-content': modeOption.id === modelValue }">{{ modeOption.displayName }}</h3>
                  <p class="agent-connection-mode-option__description" :class="modeOption.id === modelValue ? 'text-primary-content opacity-80' : 'text-base-content opacity-70'">
                    {{ modeOption.description }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';

const props = defineProps({
  modelValue: {
    type: String,
    default: 'fullmove'
  }
});

const emit = defineEmits(['update:modelValue']);

const isOpen = ref(false);
const dropdownListRef = ref(null);
const { currentLanguage } = useI18n();
const isRtl = computed(() => ['he', 'ar'].includes(currentLanguage.value) === true);

const selectorStyle = computed(() => {
  if (isRtl.value === true) {
    return { marginLeft: '8px', marginRight: '0' };
  }
  return { marginRight: '8px', marginLeft: '0' };
});

const availableModes = ref([
  {
    id: 'fullmove',
    displayName: 'Full move',
    description: 'Move the live conversation to another agent until it moves back or onward.',
    icon: 'heroicons:arrow-path-rounded-square'
  },
  {
    id: 'questions',
    displayName: 'Questions',
    description: 'Keep this agent active and ask another agent only for matching questions.',
    icon: 'heroicons:chat-bubble-left-right'
  }
]);

const selectedModeObject = computed(() => {
  const match = availableModes.value.find(modeOption => modeOption.id === props.modelValue);
  if (match !== undefined && match !== null) {
    return match;
  }
  return availableModes.value[0];
});

function toggleDropdown() {
  isOpen.value = !isOpen.value;
}

function selectModeAndClose(modeId) {
  const normalizedMode = modeId === 'questions' ? 'questions' : 'fullmove';
  if (props.modelValue !== normalizedMode) {
    emit('update:modelValue', normalizedMode);
  }
  isOpen.value = false;
}

function handleClickOutside(event) {
  if (dropdownListRef.value !== null) {
    if (isOpen.value === true) {
      if (dropdownListRef.value.contains(event.target) === false) {
        if (event.target.closest('.agent-connection-mode-selector-dropdown-container button[aria-haspopup="listbox"]') === null) {
          isOpen.value = false;
        }
      }
    }
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside, true);
});
</script>

<style scoped>
.agent-connection-mode-selector-dropdown-container {
  width: 280px;
}

.agent-connection-mode-options-list {
  max-height: 400px;
  overflow-y: auto;
}

.agent-connection-mode-trigger__content {
  display: flex;
  align-items: center;
  width: 100%;
}

.agent-connection-mode-trigger--ltr .agent-connection-mode-trigger__content {
  flex-direction: row;
}

.agent-connection-mode-trigger--rtl .agent-connection-mode-trigger__content {
  flex-direction: row-reverse;
}

.agent-connection-mode-trigger__icon {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background-color: var(--fallback-b3, #d1d5db);
  flex-shrink: 0;
}

.agent-connection-mode-trigger--ltr .agent-connection-mode-trigger__icon {
  margin-inline-end: 8px;
}

.agent-connection-mode-trigger--rtl .agent-connection-mode-trigger__icon {
  margin-inline-start: 8px;
}

.agent-connection-mode-trigger__text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.agent-connection-mode-trigger--ltr .agent-connection-mode-trigger__text {
  text-align: left;
}

.agent-connection-mode-trigger--rtl .agent-connection-mode-trigger__text {
  text-align: right;
}

.agent-connection-mode-trigger__title {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agent-connection-mode-trigger__description {
  display: block;
  font-size: 0.75rem;
  opacity: 0.7;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agent-connection-mode-trigger__chevron {
  flex-shrink: 0;
}

.agent-connection-mode-trigger--ltr .agent-connection-mode-trigger__chevron {
  margin-inline-start: auto;
}

.agent-connection-mode-trigger--rtl .agent-connection-mode-trigger__chevron {
  margin-inline-end: auto;
}

.agent-connection-mode-option {
  display: flex;
  align-items: flex-start;
}

.agent-connection-mode-option--ltr {
  flex-direction: row;
}

.agent-connection-mode-option--rtl {
  flex-direction: row-reverse;
}

.agent-connection-mode-option__icon {
  flex-shrink: 0;
}

.agent-connection-mode-option--ltr .agent-connection-mode-option__icon {
  margin-inline-end: 16px;
}

.agent-connection-mode-option--rtl .agent-connection-mode-option__icon {
  margin-inline-start: 16px;
}

.agent-connection-mode-option__text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.agent-connection-mode-option--ltr .agent-connection-mode-option__text {
  text-align: left;
}

.agent-connection-mode-option--rtl .agent-connection-mode-option__text {
  text-align: right;
}

.agent-connection-mode-option__title {
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25;
}

.agent-connection-mode-option__description {
  font-size: 0.75rem;
  margin-top: 2px;
}
</style>
